import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDefinition } from 'barte-design-system';

import { Skeleton } from '@/modules/shared/components';

import type {
  ITerminalListItem,
  TerminalRecordStatus,
  TransactionalStatus,
} from '@/modules/backoffice/types/terminal';
import type { UseTerminalListColumnsParams } from './use-terminal-list-columns.type';
import { getTerminalStatusLabel } from '@/modules/backoffice/helpers/get-terminal-status-label';

const getStatusClassName = (
  status: TerminalRecordStatus,
  styles: Record<string, string>,
) => {
  const statusClassMap: Partial<Record<TerminalRecordStatus, string>> = {
    AVAILABLE: styles['status-available'],
    LINKED: styles['status-linked'],
    ACTIVE: styles['status-active'],
    INACTIVE: styles['status-inactive'],
    TEST: styles['status-test'],
    SUPPORT: styles['status-support'],
  };

  return statusClassMap[status] || '';
};

const getTransactionalStatusClassName = (
  status: TransactionalStatus,
  styles: Record<string, string>,
) => {
  const map: Record<TransactionalStatus, string> = {
    ACTIVE: styles['txn-status-active'],
    LATE: styles['txn-status-late'],
    INACTIVE: styles['txn-status-inactive'],
  };
  return map[status] || '';
};

const formatDatePart = (value?: string | null) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString('pt-BR');
};

const formatTimePart = (value?: string | null) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const useTerminalListColumns = ({
  isLoading,
  styles,
  onCopySerialNumber,
}: UseTerminalListColumnsParams): ColumnDefinition<ITerminalListItem>[] => {
  const { t } = useTranslation();

  return useMemo(
    () => {
    const loadingColumnProps = isLoading
      ? {
          render: () => <Skeleton height="1rem" width="80%" />,
        }
      : {};

    return [
      {
        key: 'serialNumber',
        title: t('backoffice.terminalsListColumnPosId'),
        dataIndex: 'serialNumber',
        render: (_: unknown, record: ITerminalListItem) =>
          isLoading ? (
            <Skeleton height="1.25rem" width="90px" />
          ) : (
            <div className={styles['serial-cell']}>
              <button
                type="button"
                className={styles['serial-button']}
                onClick={(event) => onCopySerialNumber(event, record.serialNumber)}
                title={t('backoffice.terminalsListCopySerialTitle')}
              >
                <span className={styles['serial-primary']}>
                  {record.serialNumber || '-'}
                </span>
              </button>
            </div>
          ),
      },
      {
        key: 'status',
        title: t('backoffice.terminalsListColumnStatus'),
        dataIndex: 'status',
        render: (_: unknown, record: ITerminalListItem) =>
          isLoading ? (
            <Skeleton height="1.5rem" width="72px" />
          ) : (
            <span
              className={`${styles['status-badge']} ${getStatusClassName(
                record.status,
                styles,
              )}`}
            >
              {getTerminalStatusLabel(record.status, t)}
            </span>
          ),
      },
      {
        key: 'transactionalStatus',
        title: t('backoffice.terminalsListColumnTransactionalStatus'),
        dataIndex: 'transactionalStatus',
        render: (_: unknown, record: ITerminalListItem) =>
          isLoading ? (
            <Skeleton height="1.5rem" width="72px" />
          ) : record.transactionalStatus ? (
            <span
              className={`${styles['status-badge']} ${getTransactionalStatusClassName(record.transactionalStatus, styles)}`}
            >
              {t(`backoffice.terminalsTransactionalStatus${record.transactionalStatus}`)}
            </span>
          ) : (
            <span className={styles['last-transaction-empty']}>—</span>
          ),
      },
      {
        key: 'posCompanyName',
        title: t('backoffice.terminalsListColumnPayfac'),
        dataIndex: 'posCompanyName',
        ...loadingColumnProps,
      },
      {
        key: 'posSellerName',
        title: t('backoffice.terminalsListColumnSeller'),
        dataIndex: 'posSellerName',
        ...loadingColumnProps,
      },
      {
        key: 'lastTransactionAt',
        title: t('backoffice.terminalsListColumnLastTransaction'),
        dataIndex: 'lastTransactionAt',
        render: (_: unknown, record: ITerminalListItem) =>
          isLoading ? (
            <Skeleton height="1.25rem" width="72px" />
          ) : (
            <div className={styles['last-transaction-cell']}>
              {record.lastTransactionAt ? (
                <>
                  <span className={styles['last-transaction-date']}>
                    {formatDatePart(record.lastTransactionAt)}
                  </span>
                  <span className={styles['last-transaction-time']}>
                    {formatTimePart(record.lastTransactionAt)}
                  </span>
                </>
              ) : (
                <span className={styles['last-transaction-empty']}>—</span>
              )}
            </div>
          ),
      },
      {
        key: 'posSellerExternalId',
        title: t('backoffice.terminalsListColumnSellerId'),
        dataIndex: 'posSellerExternalId',
        ...loadingColumnProps,
      },
      {
        key: 'posTerminalExternalId',
        title: t('backoffice.terminalsListColumnTerminalId'),
        dataIndex: 'posTerminalExternalId',
        ...loadingColumnProps,
      },
    ];
    },
    [isLoading, onCopySerialNumber, styles, t],
  );
};